import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsProdSupport } from './useIsProdSupport';
import { isValidExternalRedirectUrl } from '@/router/utils';
import { BANNER_HEIGHT } from '@/components/Nav/sizes';

/**
 * Hook to calculate the total height of active banners
 *
 * Returns the combined height of:
 * - Product Support Banner (when user has product support role)
 * - Partner Redirect Banner (when there's a valid external redirect URL)
 *
 * @returns The total banner height as a CSS string (e.g., '5rem', '10rem', '0rem')
 */
export function useBannerHeight(): string {
  const isProdSupport = useIsProdSupport();
  const location = useLocation();
  const [hasRedirectUrl, setHasRedirectUrl] = useState(false);

  useEffect(() => {
    // Check URL params for redirect URL
    const urlParams = new URLSearchParams(location.search);
    const externalRedirectUrl = urlParams.get('externalRedirectUrl');

    if (
      externalRedirectUrl &&
      isValidExternalRedirectUrl(externalRedirectUrl)
    ) {
      setHasRedirectUrl(true);
      return;
    }

    // Check sessionStorage for redirect URL
    const storedRedirectUrl = sessionStorage.getItem('externalRedirectUrl');
    setHasRedirectUrl(Boolean(storedRedirectUrl));
  }, [location.search]);

  // Calculate total banner height based on active banners
  if (isProdSupport && hasRedirectUrl) {
    return `calc(${BANNER_HEIGHT} * 2)`; // Both banners = 10rem
  }

  if (isProdSupport || hasRedirectUrl) {
    return BANNER_HEIGHT; // One banner = 5rem
  }

  return '0rem'; // No banners
}

export default useBannerHeight;
